import { useState } from "react";
import type { JSX } from "react";
import { Icon } from "../ui";
import { useAppLogs } from "../../hooks/useAppLogs";
import { useWebSocket } from "../../hooks/useWebSocket";
import { getCurrentWorkspace, useWorkspaceStore } from "../../store/workspaceStore";
import { TriggerLogPopover } from "./TriggerLogPopover";
import { LogViewerPopover } from "./LogViewerPopover";

function Divider(): JSX.Element {
  return <div className="w-px h-4 bg-edge shrink-0 mx-0.5" />;
}

/** Label mapping for known origin IDs. */
const ORIGIN_LABELS: Record<string, string> = {
  telegram: "Telegram",
  google_mail: "Gmail",
  google: "Gmail",
  emails: "Emails",
  search: "Search",
  files: "Files",
};

/**
 * Application status bar — rendered at the very bottom of the app shell.
 *
 * Consumes `useAppLogs()` for real-time status and error data.
 * No mocks, no polling — all push-based via WebSocket events.
 */
export function StatusBar(): JSX.Element {
  const { latestStatus, indexProgress, errorGroups, errorCount } = useAppLogs();
  const { backendReady } = useWebSocket();
  useWorkspaceStore((state) => state.selectedWorkspaceId);
  const currentWorkspace = getCurrentWorkspace();
  const [triggerPopoverOpen, setTriggerPopoverOpen] = useState(false);
  const [errorPopoverOpen, setErrorPopoverOpen] = useState(false);
  const [logViewerOpen, setLogViewerOpen] = useState(false);
  const workspaceLabel = currentWorkspace?.label ?? null;
  const connectionLabel = backendReady ? (workspaceLabel ?? "Connected") : "Connecting…";

  return (
    <div
      className="relative flex items-center justify-between h-8 px-2
        bg-surface-tertiary border-t border-edge shrink-0 text-[12px] text-content-secondary"
    >
      {/* Left section */}
      <div className="flex items-center gap-0.5 min-w-0 flex-1">

        {/* Error badge */}
        {errorCount > 0 && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => { setErrorPopoverOpen((v) => !v); setTriggerPopoverOpen(false); setLogViewerOpen(false); }}
              className="flex items-center gap-1 px-1.5 h-6 rounded cursor-pointer
                text-red-400 hover:text-red-300 hover:bg-surface-hover/50
                transition-colors duration-[120ms] border-none bg-transparent"
              title="Errors"
            >
              <Icon name="circle-alert" size={14} />
              <span className="font-medium tabular-nums">{errorCount}</span>
            </button>
            {errorPopoverOpen && (
              <ErrorGroupsPopover
                groups={errorGroups}
                onClose={() => { setErrorPopoverOpen(false); }}
              />
            )}
          </div>
        )}

        {errorCount > 0 && <Divider />}

        {/* WS connection */}
        <div className="flex items-center gap-1 px-1.5 h-6 shrink-0">
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${backendReady ? "bg-green-500" : "bg-yellow-500 animate-pulse"}`}
          />
          <span>{connectionLabel}</span>
          {/* @tested-by: tst_fe_status_bar_001
              @invariant: E2E readiness checks require a visible Connected label even when the workspace label is shown. */}
          {backendReady && workspaceLabel != null && (
            <>
              <span className="text-content-tertiary/60">·</span>
              <span>Connected</span>
            </>
          )}
        </div>

        {/* Latest status — one message, replaces previous */}
        {latestStatus != null && (
          <>
            <Divider />
            <div className="flex items-center gap-1.5 px-1.5 h-6 min-w-0">
              <span className="text-content-tertiary">
                {ORIGIN_LABELS[latestStatus.sourceId] ?? latestStatus.sourceId}:
              </span>
              <span className="truncate tabular-nums text-content-tertiary opacity-80">
                {latestStatus.message}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Right: index progress + log viewer + triggers */}
      <div className="flex items-center gap-0.5 shrink-0">
        {/* Index progress — permanent indicator, like VS Code status */}
        {indexProgress != null && (
          <>
            <div className="flex items-center gap-1 px-1.5 h-6 text-content-tertiary tabular-nums">
              <Icon name="search" size={12} />
              <span>{indexProgress.indexed.toLocaleString()}/{indexProgress.total.toLocaleString()}</span>
            </div>
            <Divider />
          </>
        )}
        <div className="relative">
          <button
            type="button"
            onClick={() => { setLogViewerOpen((v) => !v); setTriggerPopoverOpen(false); setErrorPopoverOpen(false); }}
            className="flex items-center gap-1 px-1.5 h-6 rounded cursor-pointer
              hover:text-content hover:bg-surface-hover/50
              transition-colors duration-[120ms] border-none bg-transparent"
            title="Application logs"
          >
            <Icon name="activity" size={14} />
          </button>
          {logViewerOpen && (
            <LogViewerPopover onClose={() => { setLogViewerOpen(false); }} />
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => { setTriggerPopoverOpen((v) => !v); setErrorPopoverOpen(false); setLogViewerOpen(false); }}
            className="flex items-center gap-1 px-1.5 h-6 rounded cursor-pointer
              hover:text-content hover:bg-surface-hover/50
              transition-colors duration-[120ms] border-none bg-transparent"
            title="Recent triggers"
          >
            <Icon name="bell" size={14} />
          </button>
          {triggerPopoverOpen && (
            <TriggerLogPopover onClose={() => { setTriggerPopoverOpen(false); }} />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error groups popover (replaces ServiceErrorPopover)
// ---------------------------------------------------------------------------

import type { ErrorGroup } from "../../hooks/useAppLogs";

function ErrorGroupsPopover({
  groups,
  onClose,
}: {
  readonly groups: readonly ErrorGroup[];
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="absolute bottom-full left-0 mb-1 w-[420px] max-h-[320px] overflow-y-auto
        bg-surface-secondary border border-edge rounded-lg shadow-lg z-50 p-2"
    >
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="font-medium text-content">Errors</span>
        <button
          type="button"
          onClick={onClose}
          className="text-content-tertiary hover:text-content bg-transparent border-none cursor-pointer"
        >
          <Icon name="close" size={14} />
        </button>
      </div>
      {groups.length === 0 ? (
        <div className="text-content-tertiary px-1 py-2">No errors</div>
      ) : (
        groups.map((g) => (
          <div
            key={g.entry.id}
            className="flex items-start gap-2 px-1 py-1.5 rounded hover:bg-surface-hover/30"
          >
            <Icon name="circle-alert" size={13} className="text-red-400 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-content-secondary">
                  {ORIGIN_LABELS[g.entry.origin.id] ?? g.entry.origin.id}
                </span>
                {g.count > 1 && (
                  <span className="text-red-400 font-medium tabular-nums">
                    ×{g.count}
                  </span>
                )}
              </div>
              <div className="text-content-tertiary truncate">{g.entry.message}</div>
              <div className="text-[10px] text-content-tertiary opacity-60 mt-0.5">
                {g.count > 1
                  ? `${timeAgo(g.first_seen)} — ${timeAgo(g.last_seen)}`
                  : timeAgo(g.last_seen)}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  if (hours < 24) return `${hours}h ago`;
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return `${Math.floor(hours / 24)}d ago`;
}
